/**
 * Chinese (Simplified) dictionary — canonical source for the tenant
 * console-web key space.
 *
 * `en.ts` re-uses this dictionary's type (`typeof zhCN`) so every key
 * that exists here must exist there. Adding a new string: add the key
 * to this file first, then add the English translation. Removing a
 * string: delete from both files and also sweep call sites for the key.
 *
 * Conventions
 * - Keys are lowercase dot-paths: `feature.subfeature.detail`.
 * - Templates use `{name}` placeholders; the `t()` formatter substitutes
 *   on render. Unknown placeholders render literally so missing params
 *   fail loud in QA rather than silently eat text.
 * - Punctuation: Chinese sentences end in `。`, lists use `、`; follow
 *   the surrounding copy for spacing. English sentences follow a normal
 *   period. Do not include trailing whitespace.
 */
export const zhCN = {
  // ── <head> metadata ─────────────────────────────────────────────
  // Synced to `document.title` and `<meta name="description">` at
  // runtime by `I18nProvider`. The static value in `index.html`
  // is the zh-CN first-paint fallback; an en-preferring visitor's
  // provider effect rewrites both on mount.
  'meta.title': '哪都通控制台',
  'meta.description': '哪都通 API 控制台：管理 API 密钥、查看使用情况、充值余额并邀请团队成员。',

  // ── Common / shared ──────────────────────────────────────────────
  'common.brand.title': '哪都通控制台',
  'common.session.checking': '正在检查会话…',
  'common.back_to_login': '返回登录',
  'common.technical_details.title': '技术信息',
  'common.technical_details.show': '展开',
  'common.technical_details.hide': '收起',

  // Locale-switcher native labels live in `types.ts` as
  // `LOCALE_NATIVE_LABELS` — they are UI chrome (the name of each
  // language in its own script) rather than translatable content.

  // ── Login ───────────────────────────────────────────────────────
  'login.subtitle': '登录你的哪都通账号',
  'login.description': '登录后即可管理 API 密钥、余额、使用情况和团队成员。',
  'login.email': '邮箱',
  'login.password': '密码',
  'login.submit': '登录',
  'login.submitting': '登录中…',
  'login.need_account': '还没有账号？立即注册',
  'login.have_invite_code': '有邀请码？直接激活',
  'login.forgot': '忘记密码？',
  'login.error.empty_fields': '请填写邮箱和密码',

  // ── Login · 邮箱验证码（epic #1475）─────────────────────────────
  // 「若…则」的措辞是有意的：请求接口对已注册/未注册邮箱返回完全一致
  // 的响应，文案不能替接口把这个区分说出去。
  'login.mode.label': '登录方式',
  'login.mode.password': '密码登录',
  'login.mode.code': '邮箱验证码',
  'login.code.send': '发送验证码',
  'login.code.sending': '发送中…',
  'login.code.label': '6 位验证码',
  'login.code.ack': '若 {email} 已注册，验证码已发出，{minutes} 分钟内有效。',
  'login.code.ack_resent': '已重新发送。若 {email} 已注册，新验证码 {minutes} 分钟内有效。',
  'login.code.resend': '重新发送',
  'login.code.change_email': '换个邮箱',
  'login.code.error.empty_email': '请填写邮箱',
  'login.code.error.format': '验证码是 6 位数字',
  'login.code.error.invalid': '验证码不正确，请检查后重试',
  'login.code.error.expired': '验证码已过期，请重新获取',
  'login.code.error.attempts_exhausted': '尝试次数过多，请重新获取验证码',
  'login.code.error.send_rate_limited': '发送太频繁了，请稍后再试',
  'login.code.error.verify_rate_limited': '尝试太频繁了，请稍后再试',
  'login.code.error.account_suspended': '该账号已被停用，请联系你的客户经理',
  'login.code.error.account_deleted': '该账号已注销',

  // ── Activate（邀请码激活，epic #1475）───────────────────────────
  'activate.subtitle': '用邀请码开通账号',
  'activate.description': '填写邮箱和销售给你的邀请码，即可开通账号并领取体验金。',
  'activate.email': '邮箱',
  'activate.invite_code': '邀请码',
  'activate.submit': '激活账号',
  'activate.submitting': '激活中…',
  'activate.have_account': '已有账号？去登录',
  'activate.no_code': '没有邀请码？直接注册',
  'activate.error.empty_fields': '请填写邮箱和邀请码',
  'activate.error.code_invalid': '邀请码无效或已停用，请向你的销售确认',
  'activate.error.quota_exhausted': '该邀请码今天的名额已用完，请明天再试或联系你的销售',
  'activate.error.email_taken': '该邮箱已注册',
  'activate.error.email_taken_cta': '直接登录',
  'activate.error.rate_limited': '操作太频繁了，请稍后再试',
  'activate.success.title': '账号已开通',
  'activate.success.description': '体验金已到账，可以直接开始调用。',
  'activate.success.grant_title': '激活成功',
  'activate.success.grant_body': '体验金 {amount} 已到账，有效期至 {expiresAt}。',
  'activate.success.passwordless_hint': '该账号无需密码，下次用「邮箱验证码」登录即可。',
  'activate.success.cta': '进入控制台',

  // ── Register ────────────────────────────────────────────────────
  'register.subtitle': '创建哪都通账号',
  'register.description': '先创建你的账号，后续可以邀请团队成员一起使用。',
  'register.email': '邮箱',
  'register.password_with_hint': '密码（10–128 位）',
  'register.workspace_name': '团队名称（可选）',
  'register.workspace_placeholder': '例如：支付服务 / 数据平台 / 我的团队',
  'register.submit': '创建账号',
  'register.submitting': '创建中…',
  'register.error.empty_fields': '请填写邮箱和密码',
  'register.error.password_too_short': '密码长度至少 10 位',
  'register.success.title': '请查收邮件',
  'register.success.body':
    '我们已将验证链接发送至 {email}。打开邮件里的链接后，就可以继续进入控制台。',
  'register.success.resend_ack':
    '新的验证链接已发送。若仍未收到，请检查垃圾邮件，或稍等一分钟后再试。',
  'register.success.resend_hint': '如果暂时没收到，可以重新发送一次。',
  'register.resend.submit': '重发链接',
  'register.resend.submitting': '发送中…',
  'register.already_have_account': '已有账号?立即登录',

  // ── Forgot password ─────────────────────────────────────────────
  'forgot.subtitle': '找回登录密码',
  'forgot.description': '输入注册邮箱，我们会把重置链接发送给你。',
  'forgot.email': '邮箱',
  'forgot.submit': '发送重置链接',
  'forgot.submitting': '发送中…',
  'forgot.error.empty_email': '请填写邮箱',
  'forgot.success.title': '请查收邮件',
  'forgot.success.body': '若 {email} 存在对应账号,我们已发送重置链接。链接 24 小时内有效。',

  // ── Reset password ──────────────────────────────────────────────
  'reset.subtitle': '重新设置密码',
  'reset.description': '保存后就可以用新密码重新登录。',
  'reset.new_password': '新密码(10–128 位)',
  'reset.confirm_password': '确认新密码',
  'reset.submit': '保存新密码',
  'reset.submitting': '保存中…',
  'reset.error.missing_token': '重置链接中缺少 token',
  'reset.error.password_too_short': '密码长度至少 10 位',
  'reset.error.password_mismatch': '两次输入的密码不一致',
  'reset.error.token_banner': '缺少重置 token,请直接点击邮件中的链接,或重新申请一个。',
  'reset.success.title': '密码已更新',
  'reset.success.body': '密码已更新,请用新密码登录。',
  'reset.success.sign_in': '去登录',

  // ── Verify email ────────────────────────────────────────────────
  'verify.subtitle': '完成邮箱验证',
  'verify.description': '验证完成后，点击按钮进入控制台。',
  'verify.missing_token': '此链接缺少验证 token,请直接打开邮件中的链接,或回到登录页重新申请。',
  'verify.pending': '正在验证邮箱…',
  'verify.success.title': '邮箱验证成功',
  'verify.success.body': '你的邮箱已完成验证，账号可以正常使用了。',
  'verify.success.cta': '进入控制台',
  'verify.error.title': '验证失败',
  'verify.resend.label': '申请新的验证链接',
  'verify.resend.submit': '发送新链接',
  'verify.resend.submitting': '发送中…',
  'verify.resend.ack.title': '请查收邮件',
  'verify.resend.ack.body': '若该邮箱账号尚未验证,新的验证链接已在发送途中。',

  // ── Invitation ──────────────────────────────────────────────────
  'invite.subtitle': '加入团队',
  'invite.description': '先确认你要加入的团队，再继续完成加入流程。',
  'invite.loading': '正在加载邀请…',
  'invite.error.missing_token': 'URL 中缺少邀请 token',
  'invite.error.title': '邀请不可用',
  'invite.invite_to': '邀请你加入',
  'invite.role': '角色',
  'invite.invitee_email': '受邀邮箱',
  'invite.invited_by': '邀请者',
  'invite.expires': '过期时间',
  'invite.password_with_hint': '密码(10–128 位)',
  'invite.display_name': '显示名称(可选)',
  'invite.display_name_placeholder': '默认使用邮箱前缀',
  'invite.session_match': '你正以 {email} 登录。点击接受即可加入 {org}。',
  'invite.session_mismatch.body':
    '当前以 {current} 登录,但本邀请指向 {target}。输入该账号密码以接受邀请,或',
  'invite.session_mismatch.switch_link': '切换账号',
  'invite.session_mismatch.suffix': '。',
  'invite.accept': '接受邀请',
  'invite.join': '加入团队',
  'invite.accepting': '接受中…',
  'invite.cancel': '取消',
  'invite.error.password_too_short': '密码长度至少 {min} 位',
  'invite.error.password_too_long': '密码长度最多 {max} 位',
  'invite.toast.accepted': '邀请已接受',

  // ── Not found ──────────────────────────────────────────────────
  'notfound.title': '页面未找到',
  'notfound.body': '此控制台中不存在你访问的页面。',
  'notfound.cta': '返回仪表盘',

  // ── Shell chrome ───────────────────────────────────────────────
  'shell.brand.title': '哪都通',
  'shell.brand.subtitle': 'API 控制台',
  'shell.brand.supporting': '在一个地方管理你的密钥、余额、使用情况和团队成员。',
  'shell.nav.aria_label': '主导航',
  'shell.nav.open': '打开导航',
  'shell.nav.close': '关闭导航',
  'shell.sidebar.collapse': '收起侧栏',
  'shell.sidebar.expand': '展开侧栏',
  'shell.cmdk.trigger': '搜索页面 / 请求…',
  'shell.cmdk.placeholder': '输入页面名或 req_ 请求 ID',
  'shell.cmdk.empty': '没有匹配的结果',
  'shell.cmdk.aria_label': '命令面板',
  'shell.cmdk.footer_hint': '↑↓ 选择 · Enter 打开 · Esc 关闭',
  'shell.cmdk.group.requests': '请求',
  'shell.cmdk.group.recent': '最近访问',
  'shell.cmdk.open_request': '查看请求 {id}',
  'shell.nav.group.overview': '总览',
  'shell.nav.dashboard': '总览',
  'shell.nav.group.access': '接入',
  'shell.nav.keys': '密钥',
  'shell.nav.access_docs': '接入文档',
  'shell.nav.service_status': '服务状态',
  'shell.nav.group.usage_diagnostics': '用量与诊断',
  'shell.nav.usage': '使用情况',
  'shell.nav.group.team': '团队',
  'shell.nav.requests': '请求日志',
  'shell.nav.group.cost': '费用',
  'shell.nav.pricing': '模型定价',
  'shell.nav.billing': '余额与账单',
  'shell.nav.topup': '充值',
  'shell.nav.team_members': '成员',
  'shell.nav.team_model_permissions': '模型权限',
  'shell.nav.team_org_units': '部门',
  'shell.nav.team_audit': '审计',
  'shell.nav.group.settings': '设置',
  'shell.nav.settings': '设置',
  'shell.nav.settings_account': '账号',
  'shell.nav.settings_security': '安全',
  'shell.nav.settings_technical': '技术信息',
  'shell.org_chip.title': '当前团队',
  'shell.page.console_fallback': '控制台',
  'shell.session.active': '当前会话',
  'shell.session.verified': '邮箱已验证',
  'shell.session.unverified': '邮箱待验证',
  'shell.session.signed_in': '已登录',
  'shell.logout': '退出登录',

  // ── Dashboard ──────────────────────────────────────────────────
  'dashboard.title': '总览',
  'dashboard.header.sub': '工作区账号 {email}',
  'dashboard.retry': '重试',
  'dashboard.hero.caption': '账户余额',
  'dashboard.hero.funded': '余额充足',
  'dashboard.hero.zero': '余额为 0',
  'dashboard.hero.updated': '更新于 {timestamp}',
  'dashboard.hero.topup': '去充值',
  'dashboard.hero.zero_readonly': '余额为 0，请联系团队所有者或管理员充值。',
  'dashboard.hero.error': '余额加载失败。',
  'dashboard.kpi.month_spend.label': '本月支出',
  'dashboard.kpi.month_spend.hint': '累计消耗 {tokens} Token',
  'dashboard.kpi.active_keys.label': '活跃密钥',
  'dashboard.kpi.active_keys.hint': '管理密钥',
  'dashboard.kpi.last_request.label': '最近请求',
  'dashboard.kpi.last_request.value': '约 {time}',
  'dashboard.kpi.last_request.hint': '按小时汇总的近似时间',
  'dashboard.kpi.last_request.empty': '暂无请求',
  'dashboard.kpi.unavailable': '加载失败',
  'dashboard.status.caption': '服务状态',
  'dashboard.status.overall.healthy': '全部服务正常',
  'dashboard.status.overall.busy': '部分服务繁忙',
  'dashboard.status.overall.unavailable': '部分服务不可用',
  'dashboard.status.updated': '更新于 {timestamp}',
  'dashboard.status.view': '查看服务状态',
  'dashboard.status.error': '无法加载服务状态。',
  'dashboard.quick.caption': '快捷操作',
  'dashboard.quick.create_key': '创建密钥',
  'dashboard.quick.topup': '充值',
  'dashboard.quick.requests': '请求诊断',
  'dashboard.quick.pricing': '模型价格',
  'dashboard.trend.requests.caption': '近 7 日请求',
  'dashboard.trend.spend.caption': '近 7 日支出',
  'dashboard.trend.view_usage': '查看用量分析',
  'dashboard.trend.error': '无法加载趋势数据。',
  'dashboard.trend.empty': '暂无数据',
  'dashboard.activity.title': '最近活动',
  'dashboard.activity.view_all': '查看全部用量',
  'dashboard.activity.error': '无法加载最近活动。',
  'dashboard.activity.empty': '最近 7 天还没有请求记录。',
  'dashboard.activity.empty_hint': '先创建密钥，再让应用发起第一次调用。',
  'dashboard.activity.column.time': '时间',
  'dashboard.activity.column.requests': '请求',
  'dashboard.activity.column.tokens': 'Token',
  'dashboard.activity.column.cost': '费用',
  'dashboard.top_keys.title': '本月使用较多的密钥',
  'dashboard.top_keys.view_usage': '在用量中查看',
  'dashboard.top_keys.error': '无法加载密钥用量。',
  'dashboard.top_keys.empty': '还没有密钥用量。',
  'dashboard.top_keys.empty_hint': '开始调用后，这里会显示本月使用较多的密钥。',
  'dashboard.top_keys.column.key': '密钥',
  'dashboard.top_keys.column.requests': '请求',
  'dashboard.top_keys.column.cost': '费用',

  // ── API keys page (/access/keys) ───────────────────────────────
  'keys.title': '密钥',
  'keys.subtitle': '为你的应用创建和管理调用密钥。',
  'keys.create': '创建密钥',
  'keys.retry': '重试',
  'keys.empty.title': '还没有密钥',
  'keys.empty.hint': '密钥是应用调用哪都通 API 的凭证。创建一把密钥，填进你的工具即可开始请求。',
  'keys.column.prefix': '前缀',
  'keys.column.name': '名称',
  'keys.column.spend_limits': '消费限额',
  'keys.column.created': '创建时间',
  'keys.column.last_used': '最近使用',
  'keys.column.actions': '操作',
  'keys.never_used': '从未',
  'keys.edit': '编辑',
  'keys.revoke': '撤销',
  'keys.limit.none': '不限',
  'keys.limit.per_request.label': '单次',
  'keys.limit.daily.label': '日',
  'keys.limit.weekly.label': '周',
  'keys.limit.monthly.label': '月',
  'keys.limit.per_request.placeholder': '¥ / 次',
  'keys.limit.daily.placeholder': '¥ / 日',
  'keys.limit.weekly.placeholder': '¥ / 周',
  'keys.limit.monthly.placeholder': '¥ / 月',
  'keys.drawer.create_title': '创建密钥',
  'keys.drawer.create_description': '完整密钥只显示一次，请及时保存。模型映射可在创建后随时调整。',
  'keys.drawer.edit_title': '编辑密钥',
  'keys.drawer.edit_description': '修改名称或消费限额，只提交改动的字段。',
  'keys.drawer.close': '关闭',
  'keys.form.name': '名称',
  'keys.form.name_hint': '可选。建议按工具命名，便于追踪用量。',
  'keys.form.name_placeholder': 'production-backend',
  'keys.form.name_required': '名称不能为空',
  'keys.form.template_label': '从命名空间开始（可选）',
  'keys.form.template_none': '不预置映射',
  'keys.form.template_hint': '创建后由服务端一次性映射该命名空间当前的 {count} 个模型（别名 = 模型名）。',
  'keys.template.failure': '密钥已创建，但命名空间映射未生效：{error}',
  'keys.form.limits_label': '消费限额（人民币，可选）',
  'keys.form.limits_hint':
    '留空表示不限制。限额按实际计量的用量判定，触碰限额的那次请求仍会正常完成，密钥会在该用量计量完成后随即暂停。',
  'keys.form.limits_loading': '正在读取汇率…',
  'keys.form.limits_rate_unavailable': '汇率暂不可用，限额编辑已停用。',
  'keys.form.limit_invalid': '消费限额必须是非负人民币金额。',
  'keys.form.limit_zero': '限额不能为 0。留空表示不限制；要停止密钥消费请吊销该密钥。',
  'keys.form.network_error': '网络错误，请重试',
  'keys.form.cancel': '取消',
  'keys.form.submit_create': '创建密钥',
  'keys.form.submit_save': '保存变更',
  'keys.toast.created': '已创建密钥',
  'keys.toast.saved': '已保存变更',
  'keys.toast.revoked': '已撤销密钥',
  'keys.plaintext.title': '复制你的 API 密钥',
  'keys.plaintext.warning':
    '这是你唯一一次看到完整密钥。请立即复制并妥善保存；如果遗失，只能撤销后重新创建。',
  'keys.plaintext.name_hint': '密钥名称：{name}',
  'keys.plaintext.copied': '已复制到剪贴板',
  'keys.plaintext.copy_anywhere': '也可以直接全选密钥文本复制',
  'keys.plaintext.copy_button': '复制密钥',
  'keys.plaintext.copied_button': '已复制',
  'keys.plaintext.close': '我已保存，关闭',
  'keys.revoke.title': '撤销这把密钥？',
  'keys.revoke.description.before': '撤销后，密钥 ',
  'keys.revoke.description.after':
    ' 会立即失效；仍在使用它的应用会开始收到 401 错误。此操作无法恢复。',
  'keys.revoke.type_label': '输入密钥名称以确认撤销',
  'keys.revoke.submit': '撤销密钥',
  'keys.revoke.cancel': '取消',
  'keys.revoke.close': '关闭',
  'keys.mappings.button': '模型映射',
  'keys.mappings.title': '模型映射',
  'keys.mappings.description': '把客户端发送的模型名映射到目录模型。当前密钥：{key}',
  'keys.mappings.empty': '尚未配置映射。添加一条后，客户端就能用该别名调用对应模型。',
  'keys.mappings.column.alias': '别名',
  'keys.mappings.column.target': '目标模型',
  'keys.mappings.column.source': '来源',
  'keys.mappings.source.manual': '手动',
  'keys.mappings.source.automatic': '自动',
  'keys.mappings.automatic_managed': '由命名空间管理',
  'keys.mappings.readonly': '你可以查看映射和冲突，但当前角色无权修改。',
  'keys.mappings.namespace.title': '命名空间操作',
  'keys.mappings.namespace.description':
    '一次选择一个命名空间。「映射当前模型」只执行一次；「启用自动映射」还会自动纳入后续新增模型。两者都会去掉“命名空间/”前缀作为别名。',
  'keys.mappings.namespace.select_label': '命名空间',
  'keys.mappings.namespace.select_placeholder': '选择命名空间',
  'keys.mappings.namespace.selected_hint': '该命名空间当前可见 {count} 个模型。',
  'keys.mappings.namespace.apply': '映射当前模型',
  'keys.mappings.namespace.auto_enable': '启用自动映射',
  'keys.mappings.namespace.auto_enabled_button': '自动映射已启用',
  'keys.mappings.namespace.operations_disabled':
    '映射升级期间暂时无法映射当前模型或启用新的自动命名空间；手动映射和停用已有自动命名空间仍可使用。',
  'keys.mappings.namespace.enabled_title': '已自动映射的命名空间',
  'keys.mappings.namespace.enabled_empty': '尚未启用任何命名空间的自动映射。',
  'keys.mappings.namespace.auto_disable': '停用',
  'keys.mappings.namespace.applied': '已映射 {namespace} 当前的 {count} 个模型。',
  'keys.mappings.namespace.auto_enabled': '已为 {namespace} 启用自动映射。',
  'keys.mappings.namespace.auto_disabled': '已停用 {namespace} 的自动映射。',
  'keys.mappings.namespace.disable_title': '停用自动映射？',
  'keys.mappings.namespace.disable_description':
    '停用后，{namespace} 将不再自动提供别名；同时存在手动映射的别名仍会保留。',
  'keys.mappings.namespace.disable_confirm': '停用自动映射',
  'keys.mappings.namespace.disable_cancel': '保持启用',
  'keys.mappings.namespace.disable_close': '关闭确认框',
  'keys.mappings.conflicts.title': '发现重名的短模型名',
  'keys.mappings.conflicts.description':
    '这些别名同时指向多个模型，已从实际路由中排除。请停用一个自动命名空间或删除冲突的手动映射；完整的“命名空间/模型”名称仍可使用。',
  'keys.mappings.conflicts.delete_manual': '删除手动映射',
  'keys.mappings.conflicts.delete_title': '删除这条手动映射？',
  'keys.mappings.conflicts.delete_description':
    '将删除别名 {alias} 的手动映射。若剩余自动候选不再重名，该别名会恢复解析。',
  'keys.mappings.conflicts.delete_confirm': '删除手动映射',
  'keys.mappings.conflicts.delete_cancel': '保留映射',
  'keys.mappings.conflicts.delete_close': '关闭确认框',
  'keys.mappings.delete': '删除',
  'keys.mappings.add_title': '新增映射',
  'keys.mappings.edit_title': '修改 {alias} 的目标',
  'keys.mappings.alias_label': '别名',
  'keys.mappings.alias_placeholder': 'claude-sonnet-4-5',
  'keys.mappings.alias_hint': '客户端请求 model 字段里发送的名称，不能包含“/”。',
  'keys.mappings.alias_required': '请输入别名',
  'keys.mappings.alias_no_slash': '别名不能包含“/”',
  'keys.mappings.namespace_label': '命名空间',
  'keys.mappings.namespace_placeholder': '选择命名空间',
  'keys.mappings.sku_label': '模型',
  'keys.mappings.sku_placeholder': '选择模型',
  'keys.mappings.sku_pick_namespace_first': '先选择命名空间',
  'keys.mappings.sku_required': '请选择模型',
  'keys.mappings.submit_add': '添加映射',
  'keys.mappings.submit_save': '保存修改',
  'keys.mappings.cancel_edit': '取消编辑',
  'keys.mappings.toast.added': '已添加映射',
  'keys.mappings.toast.updated': '已更新映射',
  'keys.mappings.toast.deleted': '已删除映射',
  'keys.mappings.catalog_error': '无法加载模型目录，暂时不能新增映射。',

  // ── Service status page (/access/status) ──────────────────────
  'accessStatus.title': '服务状态',
  'accessStatus.subtitle': '区分本地配置问题和平台服务的可用性问题。',
  'accessStatus.error': '无法加载服务状态。',
  'accessStatus.retry': '重试',
  'accessStatus.kpi.overall': '总体状态',
  'accessStatus.kpi.overall_hint': '{healthy}/{total} 渠道健康',
  'accessStatus.kpi.health': '健康检查',
  'accessStatus.kpi.health_hint': '平台 API 探测',
  'accessStatus.health.ok': '正常',
  'accessStatus.health.down': '异常',
  'accessStatus.kpi.updated': '更新时间',
  'accessStatus.kpi.updated_hint': '状态数据最近一次刷新',
  'accessStatus.state.healthy': '正常',
  'accessStatus.state.busy': '繁忙',
  'accessStatus.state.unavailable': '不可用',
  'accessStatus.vendors.caption': '供应商状态',
  'accessStatus.column.vendor': '供应商',
  'accessStatus.column.channels': '健康渠道',
  'accessStatus.column.state': '状态',
  'accessStatus.events.caption': '最近事件',
  'accessStatus.events.empty': '暂无状态变化事件。',
  // #1112 — uptime-timeline view (namespace history from /v1/status).
  'accessStatus.kpi.overall_hint_ns': '{healthy}/{total} 服务正常',
  'accessStatus.timeline.caption': '服务可用性',
  'accessStatus.timeline.window': '最近 {hours} 小时',
  'accessStatus.timeline.uptime': '可用率',
  'accessStatus.timeline.window_start': '24 小时前',
  'accessStatus.timeline.window_now': '现在',
  'accessStatus.timeline.block_title': '{start}–{end} · 成功率 {rate}% · {count} 次请求',
  'accessStatus.timeline.block_no_data': '{start}–{end} · 无请求',
  'accessStatus.timeline.skus_show': '展开 {count} 个模型明细',
  'accessStatus.timeline.skus_hide': '收起模型明细',

  // ── Integration docs page (/access/docs) ──────────────────────
  'accessDocs.title': '接入文档',
  'accessDocs.subtitle':
    '从创建密钥、认识模型名与映射，到 Claude Code、Codex 和各图形客户端的接入步骤。',
  'accessDocs.toast.copied': '已复制',
  'accessDocs.copy': '复制',
  'accessDocs.toc.aria': '接入文档目录',
  'accessDocs.fact.anthropic.title': 'Anthropic Base URL',
  'accessDocs.fact.anthropic.note': '不带 /v1',
  'accessDocs.fact.openai.title': 'OpenAI Base URL',
  'accessDocs.fact.openai.note': '必须带 /v1',
  'accessDocs.fact.auth.title': '认证方式',
  'accessDocs.fact.auth.note': '完整密钥只显示一次',
  'accessDocs.fact.copy': '复制',
  'accessDocs.section.prepare.label': '准备',
  'accessDocs.section.prepare.title': '接入前准备',
  'accessDocs.section.models.label': '模型名',
  'accessDocs.section.models.title': '模型名与映射',
  'accessDocs.section.createKey.label': '创建密钥',
  'accessDocs.section.createKey.title': '创建 API 密钥',
  'accessDocs.section.claudeCode.label': 'Claude Code',
  'accessDocs.section.claudeCode.title': 'Claude Code 接入',
  'accessDocs.section.codex.label': 'Codex',
  'accessDocs.section.codex.title': 'Codex 接入',
  'accessDocs.section.ccSwitch.label': 'CC Switch',
  'accessDocs.section.ccSwitch.title': 'CC Switch 接入',
  'accessDocs.section.openclaw.label': 'OpenClaw',
  'accessDocs.section.openclaw.title': 'OpenClaw 接入',
  'accessDocs.section.clients.label': '图形客户端',
  'accessDocs.section.clients.title': '图形客户端接入',
  'accessDocs.section.claudeDesktop.label': 'Claude Desktop',
  'accessDocs.section.claudeDesktop.title': 'Claude Desktop 接入',
  'accessDocs.section.vscode.label': 'VS Code',
  'accessDocs.section.vscode.title': '在 VS Code 中使用',
  'accessDocs.section.images.label': '图片生成',
  'accessDocs.section.images.title': '图片生成与图片编辑',
  'accessDocs.images.note':
    '标准 OpenAI Images 协议：`POST /v1/images/generations` 图片生成，`POST /v1/images/edits` 图片编辑（支持 multipart 上传）。'
    + '官方 SDK 直接可用，只需把 base_url 指到上面的 OpenAI 兼容地址；模型填目录里的图片 SKU 全名。'
    + '生成远慢于文本请求，SDK 的默认超时通常不够，请显式调长。',
  'accessDocs.images.sdk': 'Python 官方 SDK（图片生成 + 图片编辑）',
  'accessDocs.images.curl': 'curl（图片生成 + 图片编辑）',
  'accessDocs.images.table.generate': 'POST /v1/images/generations 参数',
  'accessDocs.images.table.edit': 'POST /v1/images/edits 参数',
  'accessDocs.images.column.param': '参数',
  'accessDocs.images.column.required': '必填',
  'accessDocs.images.column.default': '默认值',
  'accessDocs.images.column.behaviour': '实际行为',
  'accessDocs.images.required.yes': '是',
  'accessDocs.images.required.no': '否',
  'accessDocs.images.default.none': '无',
  'accessDocs.images.param.model': '目录里图片 SKU 的完整名称（`命名空间/模型名`）。裸名只在该密钥配过模型映射时可用。',
  'accessDocs.images.param.prompt': '要生成或修改的画面描述。',
  'accessDocs.images.param.quality': '接受但不生效。响应里的 `quality` 报告实际档位。',
  'accessDocs.images.param.n': '仅支持 1。填其他值会返回 400，不会产生任何费用。',
  'accessDocs.images.param.response_format':
    '仅支持 `b64_json`。`url` 会返回 400——本路由不提供托管链接。',
  'accessDocs.images.param.background': '仅支持 `opaque`。`transparent` 会返回 400。',
  'accessDocs.images.param.size':
    '作为构图比例的提示传递，不是硬约束。响应里的 `size` 是**实际产出**的尺寸，与请求值可能不同。请以响应为准，不要假设等于请求值。',
  'accessDocs.images.param.output_format': '支持 `png` / `jpeg` / `webp`。',
  'accessDocs.images.param.output_compression': '压缩质量，仅对 `jpeg` / `webp` 生效。',
  'accessDocs.images.param.image':
    '要修改的图片，可传多张参考图。支持 multipart 文件上传，也支持 JSON 里的 base64。单次请求体上限 12 MB。',
  'accessDocs.images.param.mask': '遮罩图，标出需要重绘的区域。遮罩是提示而非精确裁剪。',
  'accessDocs.images.billing':
    '计费：按响应 `usage` 结算。`output_tokens_details.image_tokens` 是图片出量，`input_tokens` 是输入量，两者都按该图片 SKU 的费率计价——用量与账单页可逐条核对。',
  'accessDocs.section.verify.label': '验证排查',
  'accessDocs.section.verify.title': '验证与排查',
  'accessDocs.prepare.console': '控制台',
  'accessDocs.prepare.keys_page': '密钥管理',
  'accessDocs.prepare.models': '模型列表',
  'accessDocs.prepare.requests': '请求日志',
  'accessDocs.prepare.warning':
    '最常见的错误是 Base URL 填错：Anthropic 入口不要带 /v1，OpenAI 兼容入口必须带 /v1。',
  'accessDocs.prepare.anthropic_entry': 'Anthropic 入口 · 不带 /v1',
  'accessDocs.prepare.anthropic_fit':
    'Claude 系模型走这里：Claude Code、Claude Desktop 等 Anthropic 协议工具。部分 Claude 命名空间仅接受 Claude Code 客户端。',
  'accessDocs.prepare.openai_entry': 'OpenAI 入口 · 带 /v1',
  'accessDocs.prepare.openai_fit':
    'GPT 系模型走这里：Codex、OpenClaw、Cherry Studio、ChatBox、LobeChat 等 OpenAI 兼容工具。',
  'accessDocs.prepare.env_check': '命令行环境检查',
  'accessDocs.models.intro':
    '模型名有两种写法：带「/」的完整模型名（命名空间/模型，例如 {example}）在任何密钥上直接可用；不带「/」的短名只查当前密钥的模型映射表，没有对应映射会返回 404。',
  'accessDocs.models.row.canonical': '完整模型名',
  'accessDocs.models.row.canonical_value':
    '「命名空间/模型」形式，以「模型定价」页展示的名称为准，复制即可用，推荐所有新接入使用。',
  'accessDocs.models.row.alias': '短名（别名）',
  'accessDocs.models.row.alias_value':
    '给只能发送裸模型名的客户端用。需要先在密钥上配置「别名 → 目标模型」映射，别名不能包含「/」。',
  'accessDocs.models.row.manage': '映射管理',
  'accessDocs.models.row.manage_value':
    '在「密钥」页打开「模型映射」，可以一键映射某个命名空间当前的全部模型，也可以启用自动映射，让后续新增模型自动出现。重名短名会明确告警并暂停解析；停用冲突的自动命名空间或删除冲突的手动映射后即可恢复。',
  'accessDocs.models.row.pricing': '价格差异',
  'accessDocs.models.row.pricing_value':
    '同一个模型可能出现在多个命名空间且倍率不同——选模型名同时也是在选价格，详见「模型定价」页。',
  'accessDocs.models.list_snippet': '查询当前密钥可调用的模型',
  'accessDocs.models.source_of_truth':
    '每把密钥能调用什么，以 GET /v1/models 的返回为准：它列出你可见的全部完整模型名，以及这把密钥上已配置的别名。',
  'accessDocs.models.cc_lane':
    '部分 Claude 命名空间仅接受 Claude Code 客户端发出的请求，用 curl 或图形客户端调用会返回上游错误。这类模型请在 Claude Code 里使用，或改选支持通用 API 调用的命名空间。',
  'accessDocs.createKey.step1.title': '打开密钥页',
  'accessDocs.createKey.step1.body': '登录控制台后进入「密钥」页面，点击创建密钥。',
  'accessDocs.createKey.step2.title': '按需预置映射和限额',
  'accessDocs.createKey.step2.body':
    '密钥不区分供应商。可选择一个命名空间，由服务端一次性预置当前模型的短名映射；如需后续新模型自动出现，可在创建后启用自动映射。也可设置消费限额。',
  'accessDocs.createKey.step3.title': '复制并保存',
  'accessDocs.createKey.step3.body': '完整密钥只显示一次。建议按工具命名，便于追踪用量。',
  'accessDocs.createKey.row.name': '密钥名称',
  'accessDocs.createKey.row.name_value':
    '建议按工具命名，例如 claude-code-mac、codex-work、cherry-studio。',
  'accessDocs.createKey.row.template': '命名空间模板',
  'accessDocs.createKey.row.template_value':
    '可选。由服务端一次性为所选命名空间当前的每个模型预置「别名 = 模型名」映射。在「模型映射」中启用自动模式后，新模型也会自动同步；若出现重名会先告警，避免路由歧义。',
  'accessDocs.createKey.row.limits': '消费限额',
  'accessDocs.createKey.row.limits_value':
    '可按日、周、月设置人民币限额。限额按计量后的用量判定：触碰限额的那次请求仍会完成，密钥随后暂停到该周期结束。',
  'accessDocs.createKey.hint':
    '一把密钥即可调用你可见的全部模型（Claude 和 GPT 通用）。建议按工具或成员分别建密钥，便于用量归因。总览页余额为 0 时，先联系团队所有者或管理员充值。',
  'accessDocs.createKey.open_keys': '去创建密钥',
  'accessDocs.platform.aria': '操作系统',
  'accessDocs.platform.macos': 'macOS / Linux',
  'accessDocs.platform.windows': 'Windows',
  'accessDocs.claudeCode.intro':
    'Claude Code 适合在终端里处理代码阅读、修改、重构和项目级任务。接入哪都通时使用 Anthropic 入口，并把模型设置为完整模型名（命名空间/模型）。',
  'accessDocs.claudeCode.install': '安装并确认版本',
  'accessDocs.claudeCode.temp_env': '临时环境变量',
  'accessDocs.claudeCode.long_term': '长期生效配置',
  'accessDocs.claudeCode.row.model': '模型设置',
  'accessDocs.claudeCode.row.model_value':
    'ANTHROPIC_MODEL 填「模型定价」页的完整模型名；后台小模型可选设置 ANTHROPIC_SMALL_FAST_MODEL。若不设置，Claude Code 发送的默认模型名需要在密钥上有同名映射。',
  'accessDocs.claudeCode.row.first_test': '第一次测试',
  'accessDocs.claudeCode.row.first_test_value': '进入一个小项目目录，让 Claude Code 先执行只读任务。',
  'accessDocs.claudeCode.row.prompt': '推荐提示',
  'accessDocs.claudeCode.row.prompt_value':
    '先不要修改文件，请阅读项目结构，告诉我这个项目的启动方式。',
  'accessDocs.claudeCode.row.url_mistake': '常见地址错误',
  'accessDocs.claudeCode.row.url_mistake_value': 'ANTHROPIC_BASE_URL 不要误加 /v1。',
  'accessDocs.claudeCode.disable_betas': '关闭实验 beta 字段',
  'accessDocs.protocol.aria': '协议',
  'accessDocs.protocol.anthropic': 'Anthropic · Claude Code',
  'accessDocs.protocol.openai': 'OpenAI · Codex',
  'accessDocs.codex.intro':
    'Codex 类工具使用 OpenAI 兼容接口。接入哪都通时，Base URL 填 OpenAI 入口，API Key 用你的哪都通密钥，模型填完整模型名（例如 {example}）。',
  'accessDocs.codex.env.claude': 'Claude Code 环境变量',
  'accessDocs.codex.env.codex': 'Codex 环境变量',
  'accessDocs.codex.row.provider': 'Provider',
  'accessDocs.codex.row.provider_value': 'OpenAI Compatible / Custom OpenAI',
  'accessDocs.codex.row.base_url': 'Base URL / API Host',
  'accessDocs.codex.row.api_key': 'API Key',
  'accessDocs.codex.row.api_key_value': '你的哪都通 API Key，前缀为 sk-ndt-。',
  'accessDocs.codex.row.model': '模型',
  'accessDocs.ccSwitch.intro':
    'CC Switch 用来统一管理 Claude Code、Codex、OpenClaw 等工具的 Provider。接入后可以在本地切换哪都通 Claude 和 OpenAI Compatible 配置。',
  'accessDocs.ccSwitch.install': 'macOS 安装',
  'accessDocs.ccSwitch.claude_panel': 'Claude Code Provider',
  'accessDocs.ccSwitch.openai_panel': 'Codex / OpenAI Provider',
  'accessDocs.ccSwitch.row.provider_name': 'Provider 名称',
  'accessDocs.ccSwitch.row.claude_provider_name': '哪都通 Claude',
  'accessDocs.ccSwitch.row.openai_provider_name': '哪都通 OpenAI Compatible',
  'accessDocs.ccSwitch.row.app': '工具 / App',
  'accessDocs.ccSwitch.row.claude_app': 'Claude Code',
  'accessDocs.ccSwitch.row.openai_app': 'Codex / OpenClaw / OpenAI Compatible',
  'accessDocs.ccSwitch.row.protocol': 'API 协议',
  'accessDocs.ccSwitch.row.claude_protocol': 'Anthropic Compatible',
  'accessDocs.ccSwitch.row.openai_protocol': 'OpenAI Compatible',
  'accessDocs.ccSwitch.row.base_url': 'Base URL',
  'accessDocs.ccSwitch.row.api_key': 'API Key',
  'accessDocs.ccSwitch.row.claude_key': '你的哪都通 API Key（sk-ndt- 前缀）',
  'accessDocs.ccSwitch.row.openai_key': '你的哪都通 API Key（sk-ndt- 前缀，可与 Claude 共用一把）',
  'accessDocs.ccSwitch.row.model': '模型',
  'accessDocs.ccSwitch.checklist.title': '切换后没有生效时检查',
  'accessDocs.ccSwitch.checklist.1': 'CC Switch 当前选中的 Provider 是否正确。',
  'accessDocs.ccSwitch.checklist.2': '终端是否是在切换之前打开的；切换后要开新终端。',
  'accessDocs.ccSwitch.checklist.3': 'Claude Code / Codex 是否需要重启。',
  'accessDocs.ccSwitch.checklist.4': 'Base URL 是否对应协议：Anthropic 不带 /v1，OpenAI 带 /v1。',
  'accessDocs.ccSwitch.checklist.5': '控制台请求日志是否出现新的调用记录。',
  'accessDocs.openclaw.intro':
    'OpenClaw 按 OpenAI 兼容接口配置。把 Provider 指向哪都通 OpenAI Base URL，模型填完整模型名。',
  'accessDocs.openclaw.snippet': 'OpenAI 兼容 Provider',
  'accessDocs.openclaw.row.base_url': 'Base URL',
  'accessDocs.openclaw.row.api_key': 'API Key',
  'accessDocs.openclaw.row.api_key_value': '你的哪都通 API Key。',
  'accessDocs.openclaw.row.model': '模型',
  'accessDocs.openclaw.row.verify': '验证提示',
  'accessDocs.openclaw.row.verify_value': '请回复：OpenClaw 已接入。',
  'accessDocs.clients.column.tool': '工具',
  'accessDocs.clients.column.vendor': '协议',
  'accessDocs.clients.column.base': 'Base URL',
  'accessDocs.clients.column.notes': '说明',
  'accessDocs.clients.base_both': '双协议',
  'accessDocs.clients.tool.claude_code': '终端 AI 编码，使用 Anthropic 入口。',
  'accessDocs.clients.tool.codex': 'OpenAI 兼容 CLI，推荐 responses wire API。',
  'accessDocs.clients.tool.cc_switch': '统一管理 Claude Code、Codex、OpenClaw 等 Provider。',
  'accessDocs.clients.tool.openclaw': '按 OpenAI Compatible Provider 配置。',
  'accessDocs.clients.tool.cherry_studio':
    '桌面图形客户端；请选择支持通用 API 调用的模型（部分 Claude 命名空间仅限 Claude Code）。',
  'accessDocs.clients.tool.chatbox': '轻量聊天客户端，API Host 填 OpenAI 兼容地址。',
  'accessDocs.clients.tool.lobechat': '自部署或图形界面均使用 OpenAI 兼容入口。',
  'accessDocs.clients.tool.claude_desktop': '第三方网关入口；如调用报上游错误，请改用 Claude Code。',
  'accessDocs.clients.key_name': '建议 Key 名称：{name}',
  'accessDocs.clients.row.entry': '入口',
  'accessDocs.clients.row.provider_type': '服务商类型',
  'accessDocs.clients.row.provider_type_value': 'OpenAI Compatible / 自定义 OpenAI',
  'accessDocs.clients.row.api_host': 'API 地址',
  'accessDocs.clients.row.model': '模型',
  'accessDocs.clients.row.test': '测试',
  'accessDocs.clients.cherry.entry': '模型服务 / 服务商管理',
  'accessDocs.clients.cherry.test': '请用一句话说明你已经连接成功。',
  'accessDocs.clients.cherry.note':
    '部分 Claude 命名空间仅接受 Claude Code 客户端，在 Cherry Studio 里配置会报上游错误；模型名请使用「模型定价」页的完整名称。',
  'accessDocs.clients.chatbox.entry': '模型提供方 / API 设置',
  'accessDocs.clients.chatbox.test': '请回复：ChatBox 配置成功。',
  'accessDocs.clients.chatbox.note': 'API Host 填完整 OpenAI Base URL，避免漏掉或重复填写 /v1。',
  'accessDocs.clients.lobechat.entry': 'OpenAI / OpenAI Compatible',
  'accessDocs.clients.lobechat.test': '请回复：LobeChat 配置成功。',
  'accessDocs.clients.lobechat.note': '团队使用时建议按成员或项目分开创建 Key，排查消耗更清楚。',
  'accessDocs.clients.lobechat_env': 'LobeChat 自部署环境变量',
  'accessDocs.claudeDesktop.intro':
    'Claude Desktop 这里配置的是图形界面的第三方网关，不是 Claude Code 终端环境变量。如果调用报上游错误，说明所选模型仅支持 Claude Code，请改用 Claude Code 接入。',
  'accessDocs.claudeDesktop.macos.title': 'macOS 配置流程',
  'accessDocs.claudeDesktop.macos.1': '打开 Help → Troubleshooting → Enable Developer Mode。',
  'accessDocs.claudeDesktop.macos.2': '应用重启后，在欢迎界面保持 Claude Desktop 选中。',
  'accessDocs.claudeDesktop.macos.3': '打开 Developer → Configure Provider。',
  'accessDocs.claudeDesktop.macos.4':
    'Gateway base URL 填 {baseUrl}，Gateway API key 填你的哪都通 API Key。',
  'accessDocs.claudeDesktop.macos.5':
    '点击 Apply locally，选择 Relaunch now，重启后 Continue with gateway。',
  'accessDocs.claudeDesktop.windows.title': 'Windows 配置流程',
  'accessDocs.claudeDesktop.windows.1': '停留在欢迎页或邮箱输入页。',
  'accessDocs.claudeDesktop.windows.2': '按 Tab 切换到左上角菜单图标，按 Enter 打开菜单。',
  'accessDocs.claudeDesktop.windows.3':
    '选择 Developer → Configure third-party interface 或 Configure Provider。',
  'accessDocs.claudeDesktop.windows.4': 'Gateway base URL 填 {baseUrl}，不要带 /v1。',
  'accessDocs.claudeDesktop.windows.5': 'Apply locally 后按提示重启。',
  'accessDocs.claudeDesktop.row.no_menu': '找不到 Developer 菜单',
  'accessDocs.claudeDesktop.row.no_menu_value':
    '先启用 Developer Mode，并确认当前选中的是 Claude Desktop。',
  'accessDocs.claudeDesktop.row.no_gateway': 'Continue with gateway 没出现',
  'accessDocs.claudeDesktop.row.no_gateway_value': '确认点过 Apply locally 并 Relaunch。',
  'accessDocs.claudeDesktop.row.no_connect': '配置后无法连接',
  'accessDocs.claudeDesktop.row.no_connect_value': '检查 Gateway base URL 和 Key 是否完整。',
  'accessDocs.claudeDesktop.row.rejected': '请求报上游错误',
  'accessDocs.claudeDesktop.row.rejected_value':
    '所选模型所在命名空间仅接受 Claude Code 客户端。改用 Claude Code，或换支持通用客户端的模型。',
  'accessDocs.vscode.intro':
    'VS Code 本身不是模型客户端，但可以作为项目入口，在内置终端里运行 Claude Code 或 Codex。如果插件支持自定义 Provider，也按对应协议填写。',
  'accessDocs.vscode.snippet': 'VS Code 内置终端示例',
  'accessDocs.vscode.row.openai_base': 'OpenAI 兼容 Base URL',
  'accessDocs.vscode.row.anthropic_base': 'Anthropic 兼容 Base URL',
  'accessDocs.vscode.row.openai_models': 'OpenAI 模型',
  'accessDocs.vscode.row.openai_models_value': '完整模型名（命名空间/模型），以「模型定价」页为准。',
  'accessDocs.vscode.row.anthropic_models': 'Anthropic 模型',
  'accessDocs.vscode.row.anthropic_models_value':
    '同样使用完整模型名；部分 Claude 命名空间仅支持 Claude Code，插件调用会报上游错误。',
  'accessDocs.verify.note':
    '建议先用「模型名与映射」章节的 GET /v1/models 验证密钥和可用模型，再发真实请求：把示例里的「命名空间/模型名」换成 /v1/models 返回的名称。部分 Claude 命名空间仅接受 Claude Code 客户端，用 curl 直接调用这类模型会报上游错误，属预期行为。',
  'accessDocs.verify.anthropic_curl': 'Anthropic /v1/messages',
  'accessDocs.verify.openai_curl': 'OpenAI /v1/chat/completions',
  'accessDocs.verify.column.symptom': '现象',
  'accessDocs.verify.column.reason': '可能原因',
  'accessDocs.verify.column.action': '处理方式',
  'accessDocs.verify.error1.symptom': '401 / 未授权',
  'accessDocs.verify.error1.reason': 'Key 填错、复制带空格，或 Key 已被撤销。',
  'accessDocs.verify.error1.action': '重新核对密钥（前缀 sk-ndt-），必要时撤销后重建。',
  'accessDocs.verify.error2.symptom': '404 模型不存在',
  'accessDocs.verify.error2.reason': '短名没有配置映射、名称拼写错误，或该模型对你不可见。',
  'accessDocs.verify.error2.action':
    '改用「模型定价」页的完整模型名（命名空间/模型）；要用短名先在密钥上加映射；GET /v1/models 可列出这把密钥能调用的全部名称。',
  'accessDocs.verify.error3.symptom': '第三方客户端调 claude 系模型报错',
  'accessDocs.verify.error3.reason': '该模型所在命名空间仅接受 Claude Code 客户端。',
  'accessDocs.verify.error3.action': '改用 Claude Code，或换支持通用 API 的模型（见「模型定价」页）。',
  'accessDocs.verify.error4.symptom': '接口不可用 / 路径 404',
  'accessDocs.verify.error4.reason': 'Base URL 错误。',
  'accessDocs.verify.error4.action': 'Anthropic Base URL 不带 /v1；OpenAI Base URL 带 /v1。',
  'accessDocs.verify.error5.symptom': '控制台没有调用记录',
  'accessDocs.verify.error5.reason': '请求没有经过哪都通。',
  'accessDocs.verify.error5.action': '检查当前生效的 Provider、环境变量或配置文件。',
  'accessDocs.verify.error6.symptom': '402 余额不足',
  'accessDocs.verify.error6.reason': '团队余额为 0。',
  'accessDocs.verify.error6.action': '联系团队所有者或管理员充值。',
  'accessDocs.verify.error7.symptom': '403 预算受限',
  'accessDocs.verify.error7.reason':
    '密钥消费限额或成员月度限额已触发：上一次请求的用量计量后越过限额，密钥被暂停。',
  'accessDocs.verify.error7.action':
    '调高对应限额或等待周期重置，都会自动恢复（调高后约十分钟内生效）；密钥限额可在「密钥」页修改。',
  'accessDocs.verify.link.usage': '使用情况',
  'accessDocs.verify.link.usage_detail': '按密钥 / 模型查看消耗',
  'accessDocs.verify.link.requests': '请求日志',
  'accessDocs.verify.link.requests_detail': '查看每次请求和错误',
  'accessDocs.verify.link.pricing': '模型定价',
  'accessDocs.verify.link.pricing_detail': '确认当前可用模型',
  'accessDocs.verify.security':
    'API Key 只在自己的工具里使用，不要发给别人，不要贴到公开仓库、截图或群聊。每个工具单独建 Key，泄露时只需撤销重建对应那把。',

  // ── Usage page ─────────────────────────────────────────────────
  'usage.title': '使用情况',
  'usage.subtitle.admin': '从用量日志查看企业范围内的请求、Token、模型和密钥元数据。',
  'usage.subtitle.member': '查看你自己的请求、Token、模型和密钥元数据。',
  'usage.scope.admin': '企业视角',
  'usage.scope.member': '个人视角',
  'usage.window.label': '查询窗口',
  'usage.window.pending': '等待用量窗口',
  'usage.window.source': '数据源：usage logs 元数据',
  'usage.range.aria_label': '时间范围',
  'usage.range.today': '今天',
  'usage.range.7d': '7 天',
  'usage.range.30d': '30 天',
  'usage.range.90d': '90 天',
  'usage.range.month': '本月',
  'usage.range.prev_month': '上个月',
  'usage.range.next_month': '下个月',
  'usage.legend.input': '输入 Tokens',
  'usage.legend.output': '输出 Tokens',
  'usage.summary.requests': '请求数',
  'usage.summary.tokens_in': '输入 Tokens',
  'usage.summary.tokens_out': '输出 Tokens',
  'usage.summary.input_tokens': '输入 Tokens',
  'usage.summary.output_tokens': '输出 Tokens',
  'usage.summary.cache_write': '缓存写入',
  'usage.summary.cache_read': '缓存读取',
  'usage.summary.blended_spend': '综合费用',
  'usage.summary.spend': '费用',
  'usage.summary.cache_hit': '缓存命中',
  'usage.summary.cost': '费用',
  'usage.summary.active_keys': '活跃密钥',
  'usage.summary.range': '统计范围：{label}',
  'usage.summary.cost_unit': '按人民币展示',
  'usage.summary.requests_hint': '{count} 次请求',
  'usage.summary.cache_read_suffix': '+ {tokens} 缓存读取 Token',
  'usage.summary.cache_write_suffix': '+ {tokens} 缓存写入 Token',
  'usage.technical.summary': '查看缓存命中和缓存写入带来的额外 Token 统计。',
  'usage.technical.cache_read': '缓存命中 Token',
  'usage.technical.cache_write': '缓存写入 Token',
  'usage.chart.title': '请求量变化 · {label}',
  'usage.chart.subtitle': '按小时统计请求量，帮助定位短时波动。',
  'usage.chart.loading': '正在加载图表…',
  'usage.chart.error': '无法加载图表',
  'usage.model_mix.title': '按模型',
  'usage.model_mix.subtitle': '基于最近请求元数据的 Token 占比。',
  'usage.model_mix.loading': '正在加载模型分布…',
  'usage.model_mix.error': '无法加载模型分布',
  'usage.model_mix.empty': '此时间范围暂无模型元数据。',
  'usage.model_mix.requests': '{count} 次请求',
  'usage.routing.title': '服务健康',
  'usage.routing.subtitle': '基于最近请求元数据，不包含 prompt 或 response。',
  'usage.routing.error': '无法加载服务健康数据',
  'usage.routing.sampled_requests': '采样请求',
  'usage.routing.success_rate': '成功率',
  'usage.routing.avg_latency': '平均延迟',
  'usage.routing.rate_limited': '限流请求',
  'usage.routing.failed_requests': '失败请求',
  'usage.by_key.title': '按密钥查看 · {label}',
  'usage.by_key.subtitle': '按 API key 聚合请求、Token、缓存和费用。',
  'usage.by_key.loading': '正在加载分解…',
  'usage.by_key.error': '无法加载分解',
  'usage.by_key.empty': '此时间范围暂无用量。',
  'usage.by_key.count': '{count} 把密钥',
  'usage.by_key.unnamed_key': '未命名密钥',
  'usage.by_key.unknown_key': '未知密钥',
  'usage.by_key.column.key_name': '密钥名称',
  'usage.by_key.column.model': '模型',
  'usage.by_key.column.requests': '请求数',
  'usage.by_key.column.input_tokens': '输入 Tokens',
  'usage.by_key.column.output_tokens': '输出 Tokens',
  'usage.by_key.column.cache_read': '缓存命中',
  'usage.by_key.column.cache_write': '缓存写入',
  'usage.by_key.column.cost': '费用',
  'usage.requests_count': '{count} 次请求',
  'usage.succeeded_count': '{count} 次成功',
  'usage.pagination.previous': '上一页',
  'usage.pagination.next': '下一页',
  'usage.metric.total': '全部',
  'usage.metric.in': '输入',
  'usage.metric.out': '输出',
  'usage.metric.write': '写入',
  'usage.metric.read': '读取',
  'usage.error.load': '无法加载用量数据。',
  'usage.retry': '重试',
  'usage.admin.title': 'Token 使用情况',
  'usage.admin.subtitle': '汇总每位成员、密钥和请求的模型消耗。',
  'usage.admin.export.idle': '导出 CSV',
  'usage.admin.export.working': '正在导出...',
  'usage.admin.export.error': 'CSV 导出失败。',
  'usage.admin.export.done': '已导出 CSV',
  'usage.admin.window.meta': '{from} - {to} · 刷新于 {time}',
  'usage.admin.filter.model': '模型',
  'usage.admin.filter.api_key': 'API 密钥',
  'usage.admin.filter.department': '组织单元',
  'usage.admin.filter.member': '成员',
  'usage.admin.filter.all_models': '全部模型',
  'usage.admin.filter.model_all_vendors': '{model} · 全部厂商',
  'usage.admin.filter.all_keys': '全部密钥',
  'usage.admin.filter.all_departments': '全部组织单元',
  'usage.admin.filter.all_members': '全部成员',
  'usage.admin.filter.caption': '筛选',
  'usage.admin.filter.revoked_suffix': '（已吊销）',
  'usage.admin.filter.show_revoked_keys': '显示已吊销密钥',
  'usage.admin.group_by.model': '按模型分组',
  'usage.admin.group_by.department': '按组织单元分组',
  'usage.admin.group_by.member': '按成员分组',
  'usage.admin.group_by_value.model': '模型',
  'usage.admin.group_by_value.department': '组织单元',
  'usage.admin.group_by_value.member': '成员',
  'usage.admin.granularity.day': '日',
  'usage.admin.granularity.week': '周',
  'usage.admin.granularity.month': '月',
  'usage.admin.granularity.aria': '统计粒度',
  'usage.admin.summary.cache_write_detail': 'prompt 缓存创建',
  'usage.admin.summary.cache_read_detail': 'prompt 缓存复用',
  'usage.admin.summary.cost_detail': '用量日志费用元数据',
  'usage.admin.summary.seats_with_usage': '本期有用量席位',
  'usage.admin.summary.seats_roster_detail': '在册席位 {seats} · 有效密钥 {keys}',
  'usage.admin.trend.title': 'Token 使用趋势',
  'usage.admin.trend.subtitle': '按{groupBy}堆叠',
  'usage.admin.trend.total': '合计 · {tokens} tokens',
  'usage.admin.trend.metric_aria': '趋势指标',
  'usage.admin.empty.window': '此窗口暂无用量。',
  'usage.admin.model_mix.subtitle': '所选窗口内的总 Token 占比。',
  'usage.admin.model_mix.empty': '此窗口暂无模型用量。',
  'usage.admin.model_mix.io': '输入 {input} · 输出 {output}',
  'usage.admin.department_mix.title': '组织单元分布',
  'usage.admin.department_mix.subtitle': '本周期各组织单元消耗的 Tokens。',
  'usage.admin.department_mix.empty': '此窗口暂无组织单元用量。',
  'usage.admin.department_mix.unassigned': '未归属',
  'usage.admin.service.title': '服务健康',
  'usage.admin.service.subtitle': '基于计费元数据的请求质量。',
  'usage.admin.service.all_green': '全部正常',
  'usage.admin.service.requests_evaluated': '已评估请求',
  'usage.admin.service.cache_hit_rate': '缓存命中率',
  'usage.admin.service.avg_latency': '平均延迟',
  'usage.admin.service.rate_limited': '限流',
  'usage.admin.service.recovery_events': '恢复事件',
  'usage.admin.seat.title': '席位用量',
  'usage.admin.seat.subtitle': '{count} 位成员 · 按总 Token 排序',
  'usage.admin.seat.search_placeholder': '搜索姓名、邮箱、组织单元...',
  'usage.admin.seat.search_label': '搜索席位',
  'usage.admin.seat.empty': '此窗口没有匹配的成员用量。',
  'usage.admin.seat.column.member': '成员',
  'usage.admin.seat.column.team': '组织单元',
  'usage.admin.seat.column.primary_model': '主要模型',
  'usage.admin.seat.column.avg_per_request': '平均 / 请求',
  'usage.admin.seat.column.monthly_cap': '月度上限',
  'usage.admin.seat.column.trend_14d': '14天趋势',
  'usage.admin.seat.pagination_aria': '席位分页',
  'usage.member.title': '我的 Token 使用情况',
  'usage.member.subtitle': '查看你自己的用量、预算元数据、API 密钥和请求计费日志。',
  'usage.member.summary.scope_detail': '{range} 个人范围',
  'usage.member.summary.cache_write_detail': '{range} 缓存创建',
  'usage.member.summary.cache_read_detail': '{range} 缓存复用',
  'usage.member.summary.spend_detail': '来自计费元数据',
  'usage.member.summary.cache_hit_detail': '{rate} Token 命中 · {count} 次成功',
  'usage.member.trend.title': '个人 Token 趋势',
  'usage.member.keys.title': '我的 API 密钥',
  'usage.member.keys.empty': '此窗口暂无密钥用量。',
  'usage.member.keys.cache_rates': '请求 {request} · Token {token} · 读写比 {ratio}',
  'usage.member.requests.title': '最近请求',
  'usage.member.requests.empty': '此窗口暂无请求元数据。',

  // ── Request logs page ─────────────────────────────────────────
  'requests.title': '请求日志',
  'requests.subtitle': '逐条查看请求状态、模型、Token、缓存和费用。',
  'requests.range.aria_label': '请求日志时间范围',
  'requests.filter.caption': '筛选',
  'requests.list.caption': '请求记录',
  'requests.filter.request_id': '请求 ID',
  'requests.filter.request_id_placeholder': '精确搜索 request id',
  'requests.filter.key': '密钥',
  'requests.filter.key_all': '全部密钥',
  'requests.filter.status': '状态',
  'requests.filter.status_all': '全部状态',
  'requests.filter.status_success': '成功',
  'requests.filter.status_error': '错误',
  'requests.filter.clear': '清除',
  'requests.expand': '展开详情',
  'requests.collapse': '收起详情',
  'requests.empty.title': '此窗口暂无请求记录',
  'requests.empty.hint': '接入并发送第一条请求后，这里会逐条展示计费元数据。',
  'requests.empty.docs': '查看接入文档',
  'requests.empty.filtered.title': '无匹配结果',
  'requests.empty.filtered.hint': '试试放宽或清除筛选条件。',
  'requests.pagination.aria': '请求日志分页',
  'requests.key.unnamed': '未命名密钥',
  'requests.key.unknown': '未知密钥',
  'requests.error': '无法加载请求日志。',
  'requests.column.time': '时间',
  'requests.column.request': '请求 ID',
  'requests.column.key': '密钥名称',
  'requests.column.model': '模型',
  'requests.column.status': '状态',
  'requests.column.duration': '耗时',
  'requests.column.first_byte': '首包',
  'requests.column.input': '输入',
  'requests.column.output': '输出',
  'requests.column.cache_read': '缓存命中',
  'requests.column.cache_write': '缓存写入',
  'requests.column.cost': '费用',
  'requests.cost.pending': '计费中',
  'requests.cost.not_charged.not_delivered': '未收费',
  'requests.cost.not_charged.pricing_review': '复核中',
  'requests.duration.ms': '{value} ms',
  'requests.detail.request_id': '完整请求 ID',
  'requests.detail.copy_request_id': '复制请求 ID',
  'requests.detail.filter_by_key': '按此密钥筛选',
  'requests.detail.view_key_usage': '查看该密钥用量',
  'requests.detail.api_key_id': '密钥 ID',
  'requests.detail.protocol': '协议',
  'requests.detail.billing': '计费',
  'requests.detail.billing.billed': '已计费',
  'requests.detail.billing.not_billed': '未计费',
  'requests.detail.billing.pending': '计费中',
  'requests.detail.unit_billing': '按量计费',
  'requests.unit_billing.charged': '{quantity} {unit} · {amount}',
  'requests.unit_billing.unit.call': '次',
  'requests.unit_billing.unit.second': '秒',
  'requests.unit_billing.unit.character': '字符',
  'requests.unit_billing.unit.image': '张',
  'requests.unit_billing.not_charged.not_delivered': '未交付，未收费',
  'requests.unit_billing.not_charged.pricing_review': '计费复核中',
  'requests.detail.error_category': '分类',
  'requests.status_category.success': '成功',
  'requests.status_category.request_error': '请求错误',
  'requests.status_category.rate_limited': '限流',
  'requests.status_category.service_error': '服务错误',

  // ── Pricing page ──────────────────────────────────────────────
  'pricing.title': '模型定价',
  // #1706: 目录含按单位计费的模型后，副标题不再断言全表都是每百万 Tokens 量纲。
  'pricing.subtitle': '按供应商、渠道和模型查看实际扣费价格。',
  'pricing.unit': '每百万 Tokens · CNY',
  // #1706: 分组内含按单位计费行时的中性徽标；单位行自带量纲（每张/每次…）。
  'pricing.unit.neutral': '价格 · CNY',
  'pricing.loading': '正在加载模型定价…',
  'pricing.error': '无法加载模型定价。',
  'pricing.empty': '暂时还没有可显示的模型价格。',
  'pricing.no_results': '没有符合当前筛选条件的模型价格。',
  'pricing.vendor.summary': '{models} 个模型 · {channels} 个渠道',
  'pricing.vendor.no_channels': '这个供应商当前没有可用渠道。',
  'pricing.retry': '重试',
  'pricing.filter.all_vendors': '全部供应商',
  'pricing.filter.all_channels': '全部渠道',
  'pricing.filter.vendor_label': '按供应商筛选',
  'pricing.filter.channel_label': '按渠道筛选',
  'pricing.search.placeholder': '搜索模型…',
  'pricing.column.model': '模型',
  'pricing.cell.unavailable': '不可用',
  'pricing.rate.input': '输入',
  'pricing.rate.output': '输出',
  'pricing.rate.cache_read': '缓存命中',
  'pricing.rate.cache_write': '缓存写入',
  'pricing.rate.cache_write_5m': '缓存写入 5m',
  'pricing.rate.cache_write_1h': '缓存写入 1h',
  'pricing.rate.not_billed': '不计费',
  // #1706 — per-unit price labels for unit-metered SKUs; unit nouns are the
  // same closed set as requests.unit_billing.unit.* (次/秒/字符/张).
  'pricing.rate.unit.call': '每次',
  'pricing.rate.unit.second': '每秒',
  'pricing.rate.unit.character': '每字符',
  'pricing.rate.unit.image': '每张',
  'pricing.rate.unit.pending': '价格待定',
  // Epic #898 P6 (#912) — namespace-grouped SKU market view.
  'pricing.filter.all_namespaces': '全部命名空间',
  'pricing.filter.namespace_label': '按命名空间筛选',
  'pricing.namespace.summary': '{skus} 个模型',
  'pricing.column.rate': '倍率',
  'pricing.column.prices': '价格',
  'pricing.sku.discount': '折扣',

  // ── Billing page ───────────────────────────────────────────────
  'billing.title': '余额与账单',
  'billing.subtitle': '查看余额、充值、兑换记录和账单流水。',
  'billing.retry': '重试',
  'billing.balance.title': '当前余额',
  'billing.balance.status.funded': '余额充足',
  'billing.balance.status.zero': '余额不足',
  'billing.balance.error': '无法加载余额。',
  'billing.balance.updated': '更新于 {timestamp}',
  // Gift-credit visibility. `partial` is the two-gift case: the date
  // belongs to one batch, so the amount shown against it must be that
  // batch alone, not the gift total.
  'billing.gift.included': '含赠送 {amount}',
  'billing.gift.partial': '其中 {amount} {when}',
  'billing.gift.expires_in_days': '{days} 天后到期',
  'billing.gift.expires_in_day': '1 天后到期',
  'billing.gift.expires_today': '今天到期',
  'billing.gift.expired': '已过期,待回收',
  'billing.credits.title': '余额构成',
  'billing.credits.subtitle': '按入账批次拆分。扣费自上而下:先扣即将到期的赠送额度,再扣充值余额。',
  'billing.credits.error': '无法加载余额构成。',
  'billing.credits.empty': '暂无可用余额批次。',
  'billing.credits.column.kind': '来源',
  'billing.credits.column.remaining': '剩余',
  'billing.credits.column.amount': '入账金额',
  'billing.credits.column.expires': '到期',
  'billing.credits.column.issued': '入账时间',
  'billing.credits.kind.promo': '赠送',
  'billing.credits.kind.cash': '充值',
  'billing.credits.never_expires': '不过期',
  'billing.forbidden.title': '账务由企业管理员管理',
  'billing.forbidden.body':
    '成员可以管理自己的 API Key 和用量，但企业余额、充值、额度入账与账务流水需要管理员账务权限。',
  'billing.forbidden.cta': '返回仪表盘',
  'billing.topup.entry': '去充值',
  'billing.redeem.title': '兑换礼品卡',
  'billing.redeem.code_label': '礼品卡兑换码',
  'billing.redeem.code_placeholder': '32 位十六进制码',
  'billing.redeem.submit': '兑换',
  'billing.redeem.error.code_required': '请输入礼品卡兑换码',
  'billing.redeem.toast.success': '已到账 {amount},当前余额 {balance}',
  'billing.transactions.title': '余额记录',
  'billing.transactions.latest': '最新',
  'billing.transactions.page': '第 {page} 页',
  'billing.transactions.error': '无法加载交易。',
  'billing.transactions.empty': '暂无交易。',
  'billing.transactions.column.date': '时间',
  'billing.transactions.column.type': '类型',
  'billing.transactions.column.amount_usd': '金额(CNY)',
  'billing.transactions.column.running': '余额',
  'billing.transactions.newer': '较新',
  'billing.transactions.older': '较旧',
  'billing.transactions.type.charge': '请求费用',
  'billing.transactions.type.gift_card_redeem': '礼品卡入账',
  'billing.transactions.type.gift_direct_issue': '赠送入账',
  'billing.transactions.type.gift_expiry_reclaim': '赠送到期回收',
  'billing.transactions.type.adjustment': '人工调整',
  'billing.transactions.type.topup': '充值入账',
  'billing.transactions.type.external_topup': '线下充值入账',

  // ── Billing statements (credit customers, #1133) ───────────────
  'billing.statements.title': '账单',
  'billing.statements.subtitle': '按账期出具的应收账款明细。用量仅供参考,应付金额以期末应收账款为准。',
  'billing.statements.error': '无法加载账单。',
  'billing.statements.column.period': '账期',
  'billing.statements.column.consumption': '用量(参考)',
  'billing.statements.column.closing_ar': '期末应收',
  'billing.statements.column.due': '到期日',
  'billing.statements.column.status': '状态',
  'billing.statements.status.settled': '已结清',
  'billing.statements.status.open': '待结清',
  'billing.statements.status.overdue': '已逾期',
  'billing.statements.detail.title': '账单明细',
  'billing.statements.detail.period': '账期',
  'billing.statements.detail.consumption': '用量(参考)',
  'billing.statements.detail.opening_ar': '期初应收',
  'billing.statements.detail.payments': '已收款',
  'billing.statements.detail.closing_ar': '期末应收',
  'billing.statements.detail.cny_reference': '人民币参考金额',
  'billing.statements.detail.due': '到期日',
  'billing.statements.detail.close': '关闭',
  'billing.statements.detail.consumption_hint':
    '用量为该账期内的总消耗,仅供参考;实际应付金额以期末应收账款为准。',

  // ── Billing topup page ─────────────────────────────────────────
  'topup.title': '充值',
  'topup.subtitle': '使用微信支付为余额充值。下一步会显示二维码，你可以直接用手机扫码完成支付。',
  'topup.forbidden.title': '充值权限受限',
  'topup.forbidden.body': '只有团队所有者和管理员可以为余额充值。',
  'topup.forbidden.back': '返回账单',
  'topup.form.caption': '充值订单',
  'topup.amount.label': '金额(CNY · 整元)',
  'topup.amount.hint': 'CNY · 最小 {min} · 最大 {max} · 仅整元',
  'topup.usd.label': '将获得的余额价值',
  'topup.usd.hint': '按约 {rate} ¥/$ 折算；内部 USD credit 以下单时锁定的汇率为准。',
  'topup.usd.hint_loading': '正在读取当前服务端报价。',
  'topup.usd.hint_unavailable': '暂时无法读取报价；内部 credit 会在下单时由服务端锁定。',
  'topup.pay_type.legend': '支付方式',
  'topup.pay_type.wxpay': '微信支付',
  'topup.error.whole_yuan': '请输入整元金额(整数,不含小数)。',
  'topup.error.positive': '请输入正的整元金额。',
  'topup.error.required': '请输入金额以继续。',
  'topup.error.no_cents': '金额必须为整元(不能含分)。',
  'topup.error.min': '最小充值为 {amount}。',
  'topup.error.max': '最大充值为 {amount}。',
  'topup.error.no_redirect': '支付通道未返回跳转地址。',
  'topup.cancel': '取消',
  'topup.submit': '前往支付 →',
  'topup.fee_hint': '汇率会定期刷新，支付通道手续费由平台承担。',
  'topup.qr.title': '扫码支付',
  'topup.qr.order_prefix': '订单 ',
  'topup.qr.image_alt': '支付二维码',
  'topup.qr.fallback_hint': '在手机上打开微信 App,扫描下方目标 URL。',
  'topup.qr.status.paid': '已收到支付,正在跳转…',
  'topup.qr.status.other': '订单 {status}',
  'topup.qr.status.pending': '等待支付…',
  'topup.qr.status.slow': '用时超过预期…',
  'topup.qr.slow_notice':
    '为节省带宽我们已停止轮询。若你已在 App 中完成支付,余额会自行更新,可安全关闭此弹窗。',
  'topup.qr.close': '关闭',

  // ── Settings page ──────────────────────────────────────────────
  'settings.title': '设置',
  'settings.subtitle': '管理账号、安全和技术信息。',
  'settings.account.title': '账号信息',
  'settings.account.description': '这里展示你当前账号最常用的信息。',
  'settings.account.email': '邮箱',
  'settings.account.account_id': '账户 ID',
  'settings.account.org_id': '团队 ID',
  'settings.account.email_verified': '邮箱验证状态',
  'settings.account.status.verified': '已验证',
  'settings.account.status.unverified': '未验证',
  'settings.technical.summary': '原始 ID 和其他技术字段会放在这里，便于排障时查看。',
  // Remaining consumers: src/pages/Invite.tsx role labels (the rest of
  // the settings.invite_modal.* family retired with the PR 9 fork).
  'settings.invite_modal.role.member': '成员',
  'settings.invite_modal.role.admin': '管理员',
  'settings.security.title': '安全',
  'settings.security.change_password': '修改登录密码',
  'settings.security.current_password': '当前密码',
  'settings.security.new_password': '新密码(10–128 位)',
  'settings.security.confirm_password': '确认新密码',
  'settings.security.submit': '修改密码',
  'settings.security.error.current_required': '请填写当前密码',
  'settings.security.error.current_incorrect': '当前密码不正确',
  'settings.security.error.password_too_short': '密码长度至少 {min} 位',
  'settings.security.error.password_too_long': '密码长度最多 {max} 位',
  'settings.security.error.mismatch': '两次输入的密码不一致',
  'settings.security.error.network': '网络错误——请重试',
  'settings.security.toast.success': '密码已修改',

  // ── Team pages (/team/*) ────────────────────────────────────────
  // Epic #897 PR 7: the Organization mega-page's private COPY object
  // migrated into the dictionaries as the `team.*` family. Four routed
  // pages: members (+ invitations), model permissions, org units
  // (部门), audit.
  'team.retry': '重试',
  'team.forbidden': '只有所有者和管理员可以查看团队管理。',
  'team.role.owner': '所有者',
  'team.role.admin': '管理员',
  'team.role.member': '成员',

  // Members page
  'team.members.title': '成员',
  'team.members.subtitle': '把成员归属到部门，使用情况会优先显示这里的归属。',
  'team.members.invite': '邀请成员',
  'team.members.summary.members': '成员数',
  'team.members.summary.members_hint': '所有者 / 管理员 {count}',
  'team.members.summary.units': '部门数',
  'team.members.summary.units_hint': '已归属成员 {count}',
  'team.members.summary.unassigned': '未分配成员',
  'team.members.summary.unassigned_hint': '受限访问成员 {count}',
  'team.members.summary.pending': '待处理邀请',
  'team.members.directory.title': '成员目录',
  'team.members.filter.search_placeholder': '搜索姓名、邮箱、部门',
  'team.members.filter.search_label': '搜索成员',
  'team.members.filter.unit_label': '按部门筛选',
  'team.members.filter.all_units': '全部部门',
  'team.members.filter.unassigned': '未归属',
  'team.members.empty': '暂无成员。',
  'team.members.no_match': '没有匹配的成员。',
  'team.members.error.load': '无法加载成员',
  'team.members.error.units_load': '无法加载部门',
  'team.members.column.member': '成员',
  'team.members.column.unit': '部门',
  'team.members.column.role': '角色',
  'team.members.column.permissions': '模型权限',
  'team.members.column.keys': '密钥',
  'team.members.column.joined': '加入时间',
  'team.members.permission.limited': '受限',
  'team.members.permission.unrestricted': '不受限',
  'team.members.unit.aria': '调整 {member} 的部门',
  'team.members.toast.unit_updated': '已调整部门',
  'team.members.error.unit_update': '无法调整部门',

  // Member API keys (count button + dialog)
  'team.members.keys.loading': '加载中',
  'team.members.keys.error': '无法加载成员 API Keys',
  'team.members.keys.owner_note': 'API Keys 页管理',
  'team.members.keys.manage': '查看 {member} 的 API Keys',
  'team.members.keys.dialog_title': '{member} 的 API Keys',
  'team.members.keys.dialog_subtitle':
    '在成员目录里吊销该成员账号名下的 key。owner 自己的 key 仍在 API Keys 页管理。',
  'team.members.keys.close': '关闭',
  'team.members.keys.empty': '该成员暂无可用 API Key。',
  'team.members.keys.column.name': '名称',
  'team.members.keys.column.prefix': '前缀',
  'team.members.keys.column.created': '创建时间',
  'team.members.keys.column.last_used': '最后使用',
  'team.members.keys.column.actions': '操作',
  'team.members.keys.never_used': '从未使用',
  'team.members.keys.revoke': '吊销',
  'team.members.keys.revoke_title': '确认吊销 API Key',
  'team.members.keys.revoke_body': '吊销后该 key 会立即失效，调用方需要替换为新的 key。',
  'team.members.keys.revoke_confirm': '确认吊销',
  'team.members.keys.revoke_cancel': '取消',
  'team.members.keys.toast.revoked': 'API Key 已吊销',
  'team.members.keys.revoke_error': '无法吊销 API Key',

  // Pending invitations (single home on /team/members)
  'team.invitations.title': '待处理邀请',
  'team.invitations.empty': '暂无待处理邀请。仍在有效期内、尚未接受或撤销的邀请会显示在这里。',
  'team.invitations.error.load': '无法加载邀请',
  'team.invitations.column.email': '邮箱',
  'team.invitations.column.role': '邀请角色',
  'team.invitations.column.expires': '过期时间',
  'team.invitations.column.actions': '操作',
  'team.invitations.revoke': '撤销',
  'team.invitations.revoke_title': '确认撤销邀请',
  'team.invitations.revoke_body': '撤销后 {email} 将无法再通过该邀请链接加入团队。',
  'team.invitations.revoke_confirm': '确认撤销',
  'team.invitations.revoke_cancel': '取消',
  'team.invitations.close': '关闭',
  'team.invitations.toast.revoked': '邀请已撤销',
  'team.invitations.revoke_error': '无法撤销邀请',

  // Invite drawer (local rebuild of the shared invite modal flow)
  'team.invite.title': '邀请成员',
  'team.invite.description': '通过邮箱邀请新成员加入团队。',
  'team.invite.close': '关闭',
  'team.invite.email_label': '邮箱',
  'team.invite.email_placeholder': 'name@example.com',
  'team.invite.email_required': '请输入邮箱地址',
  'team.invite.role_label': '角色',
  'team.invite.role.member': '成员',
  'team.invite.role.admin': '管理员',
  'team.invite.submit': '发送邀请',
  'team.invite.cancel': '取消',
  'team.invite.toast.created': '邀请已创建',
  'team.invite.link_title': '邀请链接',
  'team.invite.link_hint': '把链接发给受邀成员；链接在过期前一次有效。',
  'team.invite.copy': '复制链接',
  'team.invite.copied': '已复制',
  'team.invite.done': '完成',

  // Model permissions page
  'team.permissions.title': '模型权限',
  'team.permissions.subtitle': '按成员或部门配置可用模型与默认模型。',
  'team.permissions.members.title': '成员',
  'team.permissions.units.title': '部门',
  'team.permissions.members.empty': '暂无成员。',
  'team.permissions.units.empty': '暂无部门。',
  'team.permissions.error.members_load': '无法加载成员',
  'team.permissions.error.units_load': '无法加载部门',
  'team.permissions.configure': '配置',
  'team.permissions.mode.inherit': '继承',
  'team.permissions.mode.all': '允许全部',
  'team.permissions.mode.custom': '自定义',
  'team.permissions.mode_help.inherit': '跟随上级策略。',
  'team.permissions.mode_help.all': '允许当前和未来新增的目录模型。',
  'team.permissions.mode_help.custom': '只允许下方手动选择的模型。',

  // Model permissions drawer (member | unit targets)
  'team.permissions.drawer.member_title': '成员模型权限',
  'team.permissions.drawer.unit_title': '部门模型权限',
  'team.permissions.drawer.member_subtitle': '配置 {target} 可用的模型。',
  'team.permissions.drawer.unit_subtitle': '配置 {target} 可继承的模型。',
  'team.permissions.drawer.close': '关闭',
  'team.permissions.drawer.mode_label': '模式',
  'team.permissions.drawer.selected_count': '已选 {count}',
  'team.permissions.drawer.default_model': '默认 {model}',
  'team.permissions.drawer.set_default': '设为默认：{model}',
  'team.permissions.drawer.search_label': '搜索模型',
  'team.permissions.drawer.search_placeholder': '搜索模型',
  'team.permissions.drawer.empty': '暂无可配置模型。',
  'team.permissions.drawer.no_search': '没有匹配的模型。',
  'team.permissions.drawer.select_group': '全选',
  'team.permissions.drawer.clear_group': '清空',
  'team.permissions.drawer.group_count': '{selected}/{total} 个模型',
  'team.permissions.drawer.legacy_title': '旧版授权',
  'team.permissions.drawer.legacy_body': '以下记录不再对应目录中的模型，保存时会原样保留。',
  'team.permissions.drawer.global_source': '全局目录',
  'team.permissions.drawer.inherited_from': '来源 {source}',
  'team.permissions.drawer.inherit_active': '正在继承 {source}',
  'team.permissions.drawer.inherit_body': '切换到自定义后才会写入该节点的覆盖规则。',
  'team.permissions.drawer.all_active': '已允许全部模型',
  'team.permissions.drawer.all_body': '未来新增到目录的模型也会自动允许。',
  'team.permissions.drawer.loading': '正在加载模型权限…',
  'team.permissions.drawer.load_error': '无法加载模型权限',
  'team.permissions.drawer.save': '保存',
  'team.permissions.drawer.cancel': '取消',
  'team.permissions.toast.member_saved': '成员模型权限已保存',
  'team.permissions.toast.unit_saved': '部门模型权限已保存',

  // Org units page (部门)
  'team.units.title': '部门',
  'team.units.subtitle': '按路径查看部门和直属成员数量。',
  'team.units.create': '新建部门',
  'team.units.empty.title': '暂无部门',
  'team.units.empty.hint': '创建部门后即可把成员归属到对应团队。',
  'team.units.error.load': '无法加载部门',
  'team.units.column.name': '名称',
  'team.units.column.parent': '上级',
  'team.units.column.members': '直属成员',
  'team.units.column.status': '状态',
  'team.units.column.actions': '操作',
  'team.units.status.active': '启用',
  'team.units.status.archived': '已归档',
  'team.units.root_parent': '顶级部门',
  'team.units.edit': '编辑',
  'team.units.archive': '归档',
  'team.units.drawer.create_title': '新建部门',
  'team.units.drawer.edit_title': '编辑部门',
  'team.units.drawer.description': '部门用于成员归属与用量归集。',
  'team.units.drawer.close': '关闭',
  'team.units.form.name_label': '名称',
  'team.units.form.name_placeholder': '例如：研发部',
  'team.units.form.name_required': '请输入部门名称',
  'team.units.form.parent_label': '上级',
  'team.units.form.submit_create': '创建',
  'team.units.form.submit_save': '保存',
  'team.units.form.cancel': '取消',
  'team.units.toast.created': '部门已创建',
  'team.units.toast.saved': '部门已更新',
  'team.units.error.save': '无法保存部门',
  'team.units.archive_title': '确认归档部门',
  'team.units.archive_body': '归档 {unit} 后，该部门将不可再用于成员归属。',
  'team.units.archive_confirm': '确认归档',
  'team.units.archive_cancel': '取消',
  'team.units.toast.archived': '部门已归档',
  'team.units.archive_error': '无法归档部门',

  // Audit page
  'team.audit.title': '审计',
  'team.audit.subtitle': '追溯成员、邀请、模型权限、Key 设置和组织结构变更。',
  'team.audit.forbidden': '当前账号没有审计日志权限。',
  'team.audit.filter.caption': '筛选',
  'team.audit.filter.action_label': '动作',
  'team.audit.filter.action_placeholder': '按 action 筛选，例如 member_model_permissions.updated',
  'team.audit.filter.target_label': '目标账号',
  'team.audit.filter.target_placeholder': '按 target account id 筛选',
  'team.audit.filter.clear': '清空',
  'team.audit.events.title': '审计日志',
  'team.audit.error.load': '无法加载审计日志',
  'team.audit.empty': '暂无审计事件。成员、邀请、Key、模型权限和组织结构变更会出现在这里。',
  'team.audit.column.time': '时间',
  'team.audit.column.actor': '操作者',
  'team.audit.column.action': '动作',
  'team.audit.column.target': '目标账号',
  'team.audit.column.detail': '元数据',
  'team.audit.actor_system': '系统',
  'team.audit.pagination.previous': '上一页',
  'team.audit.pagination.next': '下一页',
  'team.audit.pagination.aria': '审计日志分页',

  // ── i18n switcher chrome ───────────────────────────────────────
  // The aria-label on the button group IS translated so screen
  // readers announce it in the active locale rather than always
  // hearing "Language" regardless of UI language.
  'i18n.switcher.aria_label': '语言',

  // ── Toast ───────────────────────────────────────────────────────
  // Only the aria-labels are translated; toast bodies come from the
  // call site (already localised per context or surfaced as raw
  // server error messages).
  'toast.region_aria': '通知',
  'toast.dismiss_aria': '关闭通知',

  // ── Billing topup return ────────────────────────────────────────
  // Rendered at `/billing/topup/return` after a ZPay provider redirect.
  // Every card variant (pending / slow / paid / expired / failed /
  // refunded / missing-order) has its own headline + body so screen
  // readers and sighted users get a consistent narrative per state.
  'billing.topup_return.page.title': '充值结果',
  'billing.topup_return.page.order_label': '订单 ',
  'billing.topup_return.page.waiting_order_id': '等待订单号…',
  'billing.topup_return.loading.aria': '正在加载订单状态…',
  'billing.topup_return.missing.title': '缺少订单号',
  'billing.topup_return.missing.body':
    '此页面通常由支付服务商的回跳链接进入。如果你是误入,返回账务总览即可。',
  'billing.topup_return.missing.cta': '返回账务',
  'billing.topup_return.pending.title': '支付进行中',
  'billing.topup_return.pending.body': '支付正在处理。确认到账后本页会自动刷新——无需手动重载。',
  'billing.topup_return.slow.title': '支付确认比平常慢',
  'billing.topup_return.slow.body':
    '服务商尚未确认你的支付。如果已在 App 内完成支付,几分钟后余额会自动更新——可以安全关闭此页。',
  'billing.topup_return.slow.reference': '参考订单号:',
  'billing.topup_return.slow.cta.back': '返回账务',
  'billing.topup_return.slow.cta.support': '联系支持',
  'billing.topup_return.paid.title': '支付成功',
  'billing.topup_return.paid.credited': '到账',
  'billing.topup_return.paid.charged': '实付',
  'billing.topup_return.paid.at_rate': '汇率 {rate} ¥/$',
  'billing.topup_return.paid.new_balance': '新余额',
  'billing.topup_return.paid.balance_loading': '加载中…',
  'billing.topup_return.paid.technical': '技术账本明细',
  'billing.topup_return.paid.usd_credit': 'USD 入账',
  'billing.topup_return.paid.usd_ledger': 'USD 账本余额',
  'billing.topup_return.paid.redirect_hint': '数秒后将跳转至账务总览…',
  'billing.topup_return.paid.cta': '返回仪表盘',
  'billing.topup_return.terminal.expired.title': '订单已过期',
  'billing.topup_return.terminal.expired.body': '此订单在完成支付前已过期。如仍需充值请重新发起。',
  'billing.topup_return.terminal.failed.title': '支付失败',
  'billing.topup_return.terminal.failed.body':
    '支付服务商报告此订单失败。未实际扣款,可以安全重试。',
  'billing.topup_return.terminal.refunded.title': '订单已退款',
  'billing.topup_return.terminal.refunded.body': '此订单已被退款。如仍需补充余额请重新发起充值。',
  'billing.topup_return.terminal.cta.retry': '重试',
  'billing.topup_return.terminal.cta.back': '返回账务',


  // ── Skeleton ────────────────────────────────────────────────────
  'skeleton.loading': '加载中…',

  // ── Error banner ────────────────────────────────────────────────
  'error_banner.unexpected': '意外错误',
} as const;
